import { useState, type ReactNode } from 'react'
import Icon from '../docs/Icon'
import Dialog from '../ui/Dialog'
import Flag from '../marks/Flag'
import AgencyMark from '../marks/AgencyMark'
import MissionPicker from '../missions/MissionPicker'
import ThemeToggle from '../site/ThemeToggle'
import Identifier from '../site/Identifier'
import { OfficialNotice } from '../site/Banner'
import { BANNER } from '@/content/identity'
import { america, findMission } from '@/content/missions'
import { usePrefs } from '@/state/prefs'
import FormSpecimen from './FormSpecimens'

type SpecimenProps = { mini?: boolean }

function BannerSpecimen({ mini }: SpecimenProps) {
  const [open, setOpen] = useState(false)
  return (
    <div className="lib-stack">
      <div className="mds-banner lib-framed">
        <button type="button" className="mds-banner__button" aria-haspopup="dialog" onClick={() => setOpen(true)} tabIndex={mini ? -1 : undefined}>
          <Flag variant="official" className="mds-banner__flag" />
          <span>{BANNER.official}</span>
          <Icon name="info" size={16} className="mds-banner__info" />
        </button>
      </div>
      {!mini && (
        <div className="mds-banner lib-framed">
          <span className="mds-banner__button">
            <Flag variant="simple" className="mds-banner__flag" />
            <span>{BANNER.site}</span>
            <Icon name="info" size={16} className="mds-banner__info" />
          </span>
        </div>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} title="About U.S. government websites">
        <OfficialNotice />
      </Dialog>
    </div>
  )
}

function HeaderSpecimen({ mini }: SpecimenProps) {
  const { prefs } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  return (
    <div className="lib-header lib-framed">
      <span className="mds-lockup">
        <AgencyMark mission={mission} variant={prefs.mark} className="mds-lockup__mark" />
        <span className="mds-lockup__word">{mission.id === america.id ? 'Agency name' : mission.short}</span>
      </span>
      {!mini && (
        <span className="lib-header__links" aria-hidden="true">
          <span>Services</span>
          <span>Programs</span>
          <span>About</span>
        </span>
      )}
      <span className="mds-button mds-button--primary mds-button--sm">Menu</span>
    </div>
  )
}

function FooterSpecimen({ mini }: SpecimenProps) {
  return (
    <div className="lib-footer lib-framed">
      <div className="lib-footer__primary" aria-hidden={mini}>
        <span>Services</span>
        <span>About</span>
        {!mini && <span>Contact</span>}
      </div>
      <div className="lib-footer__base">
        <span>Accessibility</span>
        <span>Privacy</span>
        <span>Built by MetaPhase</span>
      </div>
    </div>
  )
}

function IdentifierSpecimen({ mini }: SpecimenProps) {
  const { prefs } = usePrefs()
  // An agency pattern: the default theme shows it for an example agency.
  const chosen = findMission(prefs.mission)
  const mission = chosen && chosen.id !== america.id ? chosen : findMission('dod')!
  if (mini)
    return (
      <div className="lib-framed lib-identifier-mini">
        <strong>{new URL(mission.url).hostname}</strong>
        <span>About · Accessibility · FOIA · No FEAR Act · OIG</span>
      </div>
    )
  return <Identifier mission={mission} />
}

function ButtonSpecimen({ mini }: SpecimenProps) {
  return (
    <div className="lib-stack">
      <div className="mds-cluster">
        <button type="button" className="mds-button mds-button--primary">Primary</button>
        <button type="button" className="mds-button mds-button--secondary">Secondary</button>
        {!mini && (
          <>
            <button type="button" className="mds-button mds-button--accent">Accent</button>
            <button type="button" className="mds-button mds-button--ghost">Ghost</button>
            <button type="button" className="mds-button mds-button--glass">Glass</button>
          </>
        )}
      </div>
      {!mini && (
        <div className="mds-cluster">
          <button type="button" className="mds-button mds-button--primary mds-button--sm">Small</button>
          <button type="button" className="mds-button mds-button--primary">Default</button>
          <button type="button" className="mds-button mds-button--primary mds-button--lg">
            Large <Icon name="arrow" size={16} />
          </button>
          <button type="button" className="mds-button mds-button--primary" disabled>
            Disabled
          </button>
        </div>
      )}
    </div>
  )
}

function PromptSpecimen({ mini }: SpecimenProps) {
  return (
    <form className="mds-prompt" role="search" onSubmit={(event) => event.preventDefault()}>
      <label className="mds-visually-hidden" htmlFor={mini ? undefined : 'lib-prompt'}>
        Ask a question
      </label>
      <input id={mini ? undefined : 'lib-prompt'} className="mds-prompt__input" placeholder="Try ‘renew my passport’" tabIndex={mini ? -1 : undefined} aria-label={mini ? 'Ask a question' : undefined} />
      <button className="mds-prompt__send" aria-label="Search" tabIndex={mini ? -1 : undefined}>
        <Icon name="arrow" />
      </button>
    </form>
  )
}

function SearchSpecimen({ mini }: SpecimenProps) {
  return (
    <form className="mds-search" role="search" onSubmit={(event) => event.preventDefault()}>
      <Icon name="search" size={18} className="mds-search__icon" />
      <input className="mds-search__input" type="search" placeholder="Search services" aria-label="Search services" tabIndex={mini ? -1 : undefined} />
      <button className="mds-button mds-button--primary mds-button--sm" tabIndex={mini ? -1 : undefined}>
        Search
      </button>
    </form>
  )
}

function CardSpecimen({ mini }: SpecimenProps) {
  const cards = [
    ['mds-card mds-card--outline', 'Apply for benefits', 'Check eligibility and apply online in about 20 minutes.'],
    ['mds-card mds-card--raised', 'Renew a passport', 'Routine renewals arrive in four to six weeks.'],
    ['mds-card', 'Find a park', 'Plan a visit to more than 400 national park sites.'],
  ]
  return (
    <div className="lib-cards">
      {cards.slice(0, mini ? 1 : 3).map(([className, title, body]) => (
        <article key={title} className={`${className} mds-card--link`}>
          <span className="mds-tag">Service</span>
          <h3 className="mds-card__title">
            {mini ? (
              title
            ) : (
              <a className="mds-card__link" href="#card">
                {title}
              </a>
            )}
          </h3>
          <p className="mds-muted">{body}</p>
        </article>
      ))}
    </div>
  )
}

function MediaCardSpecimen({ mini }: SpecimenProps) {
  return (
    <figure className={`mds-media-card${mini ? ' mds-media-card--mini' : ''}`}>
      <img className="mds-media-card__media" src="/media/hero-grand-canyon-sm.jpg" alt={mini ? '' : 'Evening light on the cliffs of the Grand Canyon.'} loading="lazy" />
      <figcaption className="mds-media-card__caption">
        <span>Grand Canyon National Park</span>
        {!mini && (
          <span className="mds-button mds-button--glass mds-button--sm">
            <Icon name="play" size={14} /> Watch
          </span>
        )}
      </figcaption>
    </figure>
  )
}

function StatSpecimen({ mini }: SpecimenProps) {
  return (
    <div className="lib-stats">
      <div className="mds-stat">
        <span className="mds-stat__value">4–6</span>
        <span className="mds-stat__label">weeks for routine renewal</span>
      </div>
      {!mini && (
        <div className="mds-stat">
          <span className="mds-stat__value">98%</span>
          <span className="mds-stat__label">of applications processed online</span>
        </div>
      )}
    </div>
  )
}

function AccordionSpecimen({ mini }: SpecimenProps) {
  const items = [
    ['Who can apply?', 'Any U.S. citizen with a valid passport issued in the last 15 years.'],
    ['How long does it take?', 'Routine service takes four to six weeks.'],
    ['Can I track my application?', 'Yes. Status updates appear within two weeks of mailing.'],
  ]
  return (
    <div className="mds-accordion">
      {items.slice(0, mini ? 2 : 3).map(([question, answer]) => (
        <details key={question} className="mds-accordion__item">
          <summary className="mds-accordion__summary" tabIndex={mini ? -1 : undefined}>
            {question}
            <span className="mds-accordion__icon" aria-hidden="true" />
          </summary>
          <p className="mds-accordion__body">{answer}</p>
        </details>
      ))}
    </div>
  )
}

function AlertSpecimen({ mini }: SpecimenProps) {
  const alerts = [
    ['', 'info', 'Offices close at 3 p.m. on Friday', 'Online services stay available.'],
    ['mds-alert--success', 'check', 'Application received', 'We sent a confirmation to your email.'],
    ['mds-alert--warning', 'clock', 'Action needed by June 30', 'Upload your proof of income.'],
    ['mds-alert--error', 'close', 'We could not verify your identity', 'Check your details and try again.'],
  ] as const
  return (
    <div className="lib-stack">
      {alerts.slice(0, mini ? 1 : 4).map(([tone, icon, title, body]) => (
        <div key={title} className={`mds-alert ${tone}`}>
          <Icon name={icon} className="mds-alert__icon" />
          <div>
            <p className="mds-alert__title">{title}</p>
            {!mini && <p className="mds-muted">{body}</p>}
          </div>
        </div>
      ))}
    </div>
  )
}

function TextInputSpecimen({ mini }: SpecimenProps) {
  const [value, setValue] = useState('')
  const [touched, setTouched] = useState(false)
  const invalid = touched && !/^\d{5}$/.test(value)
  if (mini)
    return (
      <div className="mds-field">
        <span className="mds-label">ZIP code</span>
        <span className="mds-input lib-fake-input">20500</span>
      </div>
    )
  return (
    <div className="lib-stack">
      <div className="mds-field">
        <label className="mds-label" htmlFor="lib-zip">
          ZIP code
        </label>
        <span className="mds-hint" id="lib-zip-hint">
          Five digits
        </span>
        <input
          id="lib-zip"
          className="mds-input"
          inputMode="numeric"
          value={value}
          aria-invalid={invalid}
          aria-describedby={`lib-zip-hint${invalid ? ' lib-zip-error' : ''}`}
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => setTouched(true)}
        />
        {invalid && (
          <span className="mds-error-text" id="lib-zip-error">
            Enter a five-digit ZIP code.
          </span>
        )}
      </div>
      <div className="mds-field">
        <label className="mds-label" htmlFor="lib-details">
          Tell us more
        </label>
        <textarea id="lib-details" className="mds-textarea" rows={3} />
      </div>
    </div>
  )
}

function SelectSpecimen({ mini }: SpecimenProps) {
  return (
    <div className="mds-field">
      <label className="mds-label" htmlFor={mini ? undefined : 'lib-state'}>
        State
      </label>
      <select id={mini ? undefined : 'lib-state'} className="mds-select" defaultValue="VA" tabIndex={mini ? -1 : undefined} aria-label={mini ? 'State' : undefined}>
        <option value="MD">Maryland</option>
        <option value="VA">Virginia</option>
        <option value="DC">District of Columbia</option>
      </select>
    </div>
  )
}

function ChoiceSpecimen({ mini }: SpecimenProps) {
  return (
    <div className="lib-choices">
      <fieldset className="lib-fieldset">
        <legend className="mds-label">Contact me by</legend>
        <label className="mds-choice">
          <input type="checkbox" defaultChecked tabIndex={mini ? -1 : undefined} /> Email
        </label>
        <label className="mds-choice">
          <input type="checkbox" tabIndex={mini ? -1 : undefined} /> Text message
        </label>
      </fieldset>
      {!mini && (
        <fieldset className="lib-fieldset">
          <legend className="mds-label">Delivery</legend>
          <label className="mds-choice">
            <input type="radio" name="lib-delivery" defaultChecked /> Routine
          </label>
          <label className="mds-choice">
            <input type="radio" name="lib-delivery" /> Expedited
          </label>
        </fieldset>
      )}
    </div>
  )
}

function TagSpecimen() {
  return (
    <div className="mds-cluster">
      <span className="mds-tag">Benefits</span>
      <span className="mds-tag mds-tag--outline">Renewal</span>
      <span className="mds-tag mds-tag--accent">New</span>
      <span className="mds-tag" style={{ color: 'var(--mds-success)' }}>
        <span className="mds-tag__dot" aria-hidden="true" />
        <span style={{ color: 'var(--mds-ink)' }}>Approved</span>
      </span>
    </div>
  )
}

function DialogSpecimen({ mini }: SpecimenProps) {
  const [open, setOpen] = useState<'dialog' | 'sheet' | null>(null)
  if (mini)
    return (
      <div className="lib-dialog-mini" aria-hidden="true">
        <span className="mds-h3">About this site</span>
        <span className="mds-muted">A short explanation in a focused surface.</span>
      </div>
    )
  return (
    <div className="mds-cluster">
      <button type="button" className="mds-button mds-button--secondary" onClick={() => setOpen('dialog')}>
        Open dialog
      </button>
      <button type="button" className="mds-button mds-button--secondary" onClick={() => setOpen('sheet')}>
        Open sheet
      </button>
      <Dialog open={open === 'dialog'} onClose={() => setOpen(null)} title="About this site">
        <p>Dialogs hold one short decision or explanation.</p>
        <div className="mds-cluster">
          <button type="button" className="mds-button mds-button--primary" onClick={() => setOpen(null)}>
            Done
          </button>
        </div>
      </Dialog>
      <Dialog open={open === 'sheet'} onClose={() => setOpen(null)} title="Menu" variant="sheet">
        <p>Sheets hold menus and secondary panels.</p>
      </Dialog>
    </div>
  )
}

function ThemeToggleSpecimen({ mini }: SpecimenProps) {
  const { prefs } = usePrefs()
  if (mini)
    return (
      <span className="lib-toggle-mini" aria-hidden="true">
        <Icon name={prefs.theme === 'dark' ? 'moon' : 'sun'} />
      </span>
    )
  return (
    <div className="mds-cluster">
      <ThemeToggle />
      <span className="mds-muted">Current theme: {prefs.theme}</span>
    </div>
  )
}

function MissionPickerSpecimen({ mini }: SpecimenProps) {
  const { prefs } = usePrefs()
  const [open, setOpen] = useState(false)
  const mission = findMission(prefs.mission) ?? america
  return (
    <>
      <button type="button" className="mds-mission-chip" onClick={() => setOpen(true)} aria-haspopup="dialog" tabIndex={mini ? -1 : undefined}>
        <span className="mds-mission-chip__swatch" aria-hidden="true" />
        <span>{mission.id === america.id ? 'Choose mission' : mission.name}</span>
      </button>
      {!mini && <MissionPicker open={open} onClose={() => setOpen(false)} />}
    </>
  )
}

function SegmentedSpecimen({ mini }: SpecimenProps) {
  const [value, setValue] = useState('Week')
  return (
    <div className="mds-segmented" role="group" aria-label="Period">
      {['Week', 'Month', 'Year'].map((option) => (
        <button key={option} type="button" className="mds-segmented__option" aria-pressed={value === option} onClick={() => setValue(option)} tabIndex={mini ? -1 : undefined}>
          {option}
        </button>
      ))}
    </div>
  )
}

function BreadcrumbSpecimen({ mini }: SpecimenProps) {
  return (
    <nav className="mds-breadcrumb" aria-label={mini ? undefined : 'Breadcrumb example'} aria-hidden={mini || undefined}>
      <ol>
        <li>
          <a href="#breadcrumb" tabIndex={mini ? -1 : undefined}>
            Home
          </a>
        </li>
        <li>
          <a href="#breadcrumb" tabIndex={mini ? -1 : undefined}>
            Services
          </a>
        </li>
        <li aria-current="page">Passports</li>
      </ol>
    </nav>
  )
}

function StepsSpecimen({ mini }: SpecimenProps) {
  const [step, setStep] = useState(1)
  const steps = ['Eligibility', 'Details', 'Documents', 'Review']
  return (
    <div className="mds-steps">
      <ol className="mds-steps__list">
        {steps.map((name, index) => (
          <li
            key={name}
            className={`mds-steps__segment${index < step ? ' is-complete' : ''}${index === step ? ' is-current' : ''}`}
            aria-current={index === step ? 'step' : undefined}
          >
            <span className="mds-steps__name">{name}</span>
            <span className="mds-visually-hidden">{index < step ? ', completed' : index === step ? ', current' : ', not completed'}</span>
          </li>
        ))}
      </ol>
      <p className="mds-steps__heading">
        <span className="mds-steps__count">
          Step {step + 1} of {steps.length}:
        </span>{' '}
        {steps[step]}
      </p>
      {!mini && (
        <div className="mds-cluster">
          <button type="button" className="mds-button mds-button--secondary mds-button--sm" disabled={step === 0} onClick={() => setStep(step - 1)}>
            Back
          </button>
          <button type="button" className="mds-button mds-button--primary mds-button--sm" disabled={step === steps.length - 1} onClick={() => setStep(step + 1)}>
            Continue
          </button>
        </div>
      )}
    </div>
  )
}

function PaginationSpecimen({ mini }: SpecimenProps) {
  const [page, setPage] = useState(2)
  const pages = mini ? [1, 2, 3] : [1, 2, 3, 4, 5]
  return (
    <nav className="mds-pagination" aria-label={mini ? undefined : 'Pagination example'} aria-hidden={mini || undefined}>
      <button type="button" className="mds-pagination__link" disabled={page === 1} onClick={() => setPage(page - 1)} tabIndex={mini ? -1 : undefined}>
        <span aria-hidden="true">‹</span> Previous
      </button>
      {pages.map((number) => (
        <button
          key={number}
          type="button"
          className="mds-pagination__link"
          aria-current={number === page ? 'page' : undefined}
          aria-label={`Page ${number}`}
          onClick={() => setPage(number)}
          tabIndex={mini ? -1 : undefined}
        >
          {number}
        </button>
      ))}
      {!mini && <span className="mds-pagination__gap">…</span>}
      <button type="button" className="mds-pagination__link" disabled={page === pages.length} onClick={() => setPage(page + 1)} tabIndex={mini ? -1 : undefined}>
        Next <span aria-hidden="true">›</span>
      </button>
    </nav>
  )
}

const specimens: Record<string, (props: SpecimenProps) => ReactNode> = {
  'banner': BannerSpecimen,
  header: HeaderSpecimen,
  footer: FooterSpecimen,
  identifier: IdentifierSpecimen,
  button: ButtonSpecimen,
  'prompt-pill': PromptSpecimen,
  search: SearchSpecimen,
  card: CardSpecimen,
  'media-card': MediaCardSpecimen,
  stat: StatSpecimen,
  accordion: AccordionSpecimen,
  alert: AlertSpecimen,
  'text-input': TextInputSpecimen,
  select: SelectSpecimen,
  'checkbox-radio': ChoiceSpecimen,
  tag: TagSpecimen,
  'dialog-sheet': DialogSpecimen,
  'theme-toggle': ThemeToggleSpecimen,
  'mission-picker': MissionPickerSpecimen,
  'segmented-control': SegmentedSpecimen,
  breadcrumb: BreadcrumbSpecimen,
  'step-indicator': StepsSpecimen,
  pagination: PaginationSpecimen,
}

export default function Specimen({ id, mini = false }: { id: string; mini?: boolean }) {
  const Render = specimens[id]
  return Render ? <Render mini={mini} /> : <FormSpecimen id={id} mini={mini} />
}
